import { InfoIcon, Text, WarningIcon } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { hatcheryTextModes } from "@/app/theme";
import { CopyButton } from "@/components/buttons";
import type { FolderReadFailure } from "@/upload";
import styles from "@/page/datasetUpload/datasetUpload.module.css";
import { buildChownCommand } from "./chownCommand";

type FolderReadGuideProps = {
  // 폴더 선택 직후의 읽기 확인 결과. null 이면 아직 발견한 문제가 없다(예방 안내만 표시한다).
  failure: FolderReadFailure | null;
  // 복구 명령에 넣을 선택한 폴더명. 알 수 없으면 자리표시자를 쓴다.
  folderName: string | null;
};

/**
 * 드롭존 아래의 소유권 안내. 평소에는 예방 안내(info)를 표시한다. 읽기 실패를 확인하면 같은
 * 자리에서 원인과 대상 폴더를 알리는 안내(위험)로 바뀐다. 같은 자리에서 톤만 바꾸는 안내이므로
 * 두 상태가 같은 구조(아이콘 + 본문)를 쓴다.
 *
 * 소유자가 다른 파일은 읽기 권한이 있어도 브라우저가 열지 못한다. 그 실패는 전송 단계에서
 * network error 로만 나타난다. 사용자가 원인을 알 수 있는 곳은 여기뿐이다.
 */
export function FolderReadGuide({ failure, folderName }: FolderReadGuideProps): ReactNode {
  if (failure === null) {
    return (
      <Callout tone="info">
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.uploadMeta}>
          Docker 컨테이너가 root 권한으로 만든 폴더는 읽기 권한이 있어도 브라우저가 열지 못합니다.
          폴더가 있는 위치에서 소유자를 바꾼 뒤 선택해 주세요.
        </Text>
        <CommandRow command={buildChownCommand(null)} />
      </Callout>
    );
  }

  if (failure.reason === "missing") {
    return (
      <Callout tone="danger">
        <Text variant={hatcheryTextModes["body-bold"]}>선택한 뒤 폴더 내용이 바뀌었습니다</Text>
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.uploadMeta}>
          일부 파일이 옮겨졌거나 지워져 열 수 없습니다. 폴더를 다시 선택해 주세요.
        </Text>
        <ExamplePaths failure={failure} />
      </Callout>
    );
  }

  return (
    <Callout tone="danger">
      <Text variant={hatcheryTextModes["body-bold"]}>
        {failure.everySampleFailed
          ? "선택한 폴더의 파일을 읽을 수 없습니다"
          : "선택한 폴더에 읽을 수 없는 파일이 있습니다"}
      </Text>
      <Text variant={hatcheryTextModes["caption-normal"]} className={styles.uploadMeta}>
        읽기 권한이 있어도 파일 소유자가 다르면 브라우저가 열지 못합니다. Docker 컨테이너가 root
        권한으로 만든 폴더에서 주로 생깁니다.
      </Text>
      <CommandRow command={buildChownCommand(folderName)} />
      <Text variant={hatcheryTextModes["caption-normal"]} className={styles.uploadMeta}>
        폴더가 있는 위치에서 실행한 뒤 폴더를 다시 선택해 주세요.
      </Text>
      <ExamplePaths failure={failure} />
    </Callout>
  );
}

// 아이콘 열과 본문 열 두 칸. 위험 톤만 role="alert" 로 알린다. 예방 안내는 항상 표시되므로
// 새로 발생한 이벤트가 아니다.
function Callout({ tone, children }: { tone: "info" | "danger"; children: ReactNode }): ReactNode {
  const isInfo = tone === "info";
  return (
    <div
      className={isInfo ? styles.readHint : styles.readNotice}
      role={isInfo ? undefined : "alert"}
    >
      <span className={styles.readIcon} aria-hidden="true">
        {isInfo ? <InfoIcon /> : <WarningIcon />}
      </span>
      <div className={styles.readBody}>{children}</div>
    </div>
  );
}

function CommandRow({ command }: { command: string }): ReactNode {
  return (
    <div className={styles.readNoticeCommand}>
      <code className={styles.readNoticeCode}>{command}</code>
      <CopyButton mode="icon" text={command} ariaLabel="소유자 변경 명령 복사" />
    </div>
  );
}

// 표본이 모두 실패했으면 예시가 새 정보를 주지 않는다. 일부만 실패했을 때만 어느 파일인지 표시한다.
function ExamplePaths({ failure }: { failure: FolderReadFailure }): ReactNode {
  if (failure.everySampleFailed || failure.paths.length === 0) {
    return null;
  }
  return (
    <Text variant={hatcheryTextModes["caption-normal"]} className={styles.uploadMeta}>
      {`예: ${failure.paths.join(", ")}`}
    </Text>
  );
}
